/**
 * ProShip Direct v2 — the states every module page can land in.
 *
 * Extracted rather than repeated: the setup shell, My Couriers and the rule editor all need
 * "plan is off", "no accounts yet" and "the courier catalogue failed to load", and a third
 * copy of any of them would be the near-duplicate the standards call debt (§4.1).
 */
import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { Link } from 'react-router-dom';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import { sectionCardSx, sectionTitleSx, primaryBtnSx, secondaryBtnSx } from '../../../styles/patterns';
import { SUBTLE } from '../../../styles/foundation/colors';
import { FONT, SIZE } from '../../../styles/foundation/typography';
import { GAP } from '../../../styles/foundation/spacing';
import { dpPath } from '../flags';

const cardSx = {
  ...sectionCardSx,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: GAP.md,
};

const bodySx = { fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.6 };

/** Setup and courier management have no meaning before the plan is switched on. */
export function PlanInactive({ what = 'This' }) {
  return (
    <Box sx={cardSx}>
      <Typography sx={{ ...sectionTitleSx, mb: 0 }}>ProShip Direct is not active yet</Typography>
      <Typography sx={bodySx}>
        {what} applies to courier accounts you bring yourself, so it opens once the plan is active.
        ProShip&rsquo;s couriers keep working either way — nothing here affects them.
      </Typography>
      <Button
        component={Link}
        to={dpPath('/home')}
        variant='contained'
        size='small'
        startIcon={<RocketLaunchOutlinedIcon />}
        sx={primaryBtnSx}
      >
        See the plan
      </Button>
    </Box>
  );
}

export function NoAccounts() {
  return (
    <Box sx={cardSx}>
      <Typography sx={{ ...sectionTitleSx, mb: 0 }}>No courier accounts of your own yet</Typography>
      <Typography sx={bodySx}>
        Connect a courier contract to get started. Until then every shipment runs on ProShip&rsquo;s
        couriers, which need nothing from you.
      </Typography>
      <Button
        component={Link}
        to={dpPath('/couriers/connect')}
        variant='contained'
        size='small'
        startIcon={<AddOutlinedIcon />}
        sx={primaryBtnSx}
      >
        Connect a courier
      </Button>
    </Box>
  );
}

/** The ProShip courier catalogue is a live read, so its failure needs a real path. */
export function CatalogueError({ onRetry }) {
  return (
    <Box sx={cardSx}>
      <Typography sx={{ ...sectionTitleSx, mb: 0 }}>Could not load the ProShip courier list</Typography>
      <Typography sx={bodySx}>
        Your own accounts are unaffected and still shown. ProShip couriers cannot be listed or
        switched until this loads.
      </Typography>
      <Button size='small' onClick={onRetry} startIcon={<RefreshOutlinedIcon />} sx={secondaryBtnSx}>
        Try again
      </Button>
    </Box>
  );
}
